/* eslint-disable @typescript-eslint/consistent-type-assertions */
import dayjs from 'dayjs';
import { Clinician } from '../../models/clinician.model';
import { Facility } from '../../models/facility.model';
import { Job } from '../../models/job.model';
import { MarginCalculatorDetailModel } from '../../models/margin-calculator-detail.model';
import { MarginCalculatorExpenseAllModel } from '../../models/margin-calculator-expense-all.model';
import { MarginCalculatorModel } from '../../models/margin-calculator.model';
import { ProfessionSpecialtyModel } from '../../models/specialty.model';
import { HousingStateHelpers } from './housing-state-helpers';
import { MarginState } from './margin-state';
import { ReimbursementsState } from './reimbursements.state';
import { MatCalendarCellCssClasses } from '@angular/material/datepicker';
import { StipendState } from './stipend-state';
import { FormulaHelperService } from '../../services/formula-helper.service';
import { BaseBillingRate, CERT_OTHER_ID, CERT_OTHER_NAME, EducationReimbursement } from '../../models';
import Big from 'big.js';
import { NumberHelperService } from '../../services/number-helper.service';
import { PayBillingStateHelpers } from './pay-billing-state-helper';
import { DateHelpers } from 'src/app/shared/helpers';

export const dateFormat = 'M/D/YYYY';

export class MarginCalculatorStateHelpers {
    static convertToModel(margin: MarginState): MarginCalculatorModel {
        return {
            id: margin?.id,
            userId: margin?.clinician?.userId,
            fileName: margin?.fileName,
            marginPct: margin?.marginPct,
            marginDollars: margin?.marginDollars,
            marginBillRate: margin?.baseBillingRate?.value ?? 0,
            marginPayRate: margin?.marginPayRate,
            targetMargin: margin?.targetMargin,
            dateCreated: new Date(margin?.dateCreated),
            createdBy: margin?.createdBy,
            dateModified: new Date(margin?.dateModified),
            modifiedBy: margin?.modifiedBy,
            isDeleted: margin?.isDeleted,
            isPipelined: margin?.isPipelined,
            dateDeleted: new Date(margin?.dateDeleted),
            deletedBy: margin?.deletedBy,
            version: margin?.version,
            contractId: margin?.contractId,
            contractStageCode: margin?.contractStageCode,
            contractStageDescription: margin?.contractStageDescription,
            datePipelined: new Date(margin?.datePipelined),
            isLinkedContractRequestInDraftingStatus: margin?.isLinkedContractRequestInDraftingStatus,
            openContractRequestId: margin?.openContractRequestId,
            isOfferMargin: margin?.isOfferMargin,
            offerId: margin?.offerId,
            jobId: margin?.jobId,
            duplicatedFromMarginId: margin?.duplicatedFromMarginId,
            importedFromMarginId: margin?.importedFromMarginId,
            marginTypeId: margin?.marginTypeId,
            detail: {
                id: margin?.id,
                hospitalId: margin?.facility?.hospitalId,
                originalHospitalId: margin?.originalHospitalId,
                isRate: margin?.isRate,
                state: margin?.facility?.hospitalState,
                isBillOverTime: margin?.isBillOverTime,
                billDorW: MarginCalculatorStateHelpers.convertBillingWeekOrDayRuleFromState(margin?.billDorW),
                overTimeRate: margin?.overTimeRate,
                shifts: margin?.shifts ?? 0,
                hours: margin?.hours ?? 0,
                orientationHours: margin?.orientationHours,
                furniture: margin?.housing?.furniture,
                furnitureDelivery: margin?.housing?.furnitureDelivery,
                utilities: margin?.housing?.utilities,
                relocation: margin?.relocation,
                licenseReimbursement: margin?.licenseReimbursement,
                monthlyHousing: margin?.housing?.monthlyHousing,
                extendedStayHotel: margin?.housing?.extendedStayHotel,
                bonus: margin?.bonus,
                housingDeposit: margin?.housingDeposit,
                meals: margin?.stipends?.mealStipendPerDay ?? 0,
                housingStipend: margin?.stipends?.housingStipendPerDay,
                tittersPPD: margin?.tittersPPD,
                isCompBonus: margin?.isCompBonus,
                weeks: margin?.weeks,
                lastWeeksLength: margin?.weeks,
                isExtension: margin?.isExtension,
                target: margin?.target,
                isBenefit: margin?.isBenefit,
                isMedical: margin?.isMedical,
                age: margin?.age,
                medicalCoverage: margin?.medicalCoverage,
                medicalMonth: margin?.medicalMonth,
                isDental: margin?.isDental,
                dentalCoverage: margin?.dentalCoverage,
                dentalMonth: margin?.dentalMonth,
                stipendB: margin?.stipendB,
                certReimb: margin?.certReimb,
                rentalCar: margin?.reimbursements?.rentalCar,
                shipping: margin?.shipping,
                giftCard: margin?.giftCard,
                specialty: margin?.specialty?.id,
                premiumRate: margin?.premiumRate,
                premiumBillRate: margin?.premiumBillRate,
                estTaxRate: margin?.taxRate,
                shiftTime: margin?.shiftTime,
                oTMult: margin?.oTMult,
                vmsFees: margin?.vmsFees,
                isAyaHousing: margin?.housing?.isAyaHousing,
                extDiscount: margin?.extDiscount,
                extDiscountType: margin?.extDiscountType,
                discountPercent: margin?.discountPercent,
                andOr: margin?.andOr,
                billDorWM: MarginCalculatorStateHelpers.convertBillingWeekOrDayRuleFromState(margin?.billDorWM),
                dateFrom: DateHelpers.createUTCDateFromLocalDate(new Date(margin?.startDate)),
                dateTo: DateHelpers.createUTCDateFromLocalDate(new Date(margin?.endDate)),
                nIH: margin?.nIH,
                finger: margin?.finger,
                hWares: margin?.hWares,
                altShift: margin?.altShift,
                shiftsAlt: margin?.shiftsAlt ?? 0,
                hoursAlt: margin?.hoursAlt ?? 0,
                shiftTimeAlt: margin?.shiftTimeAlt,
                billRateAlt: margin?.baseBillingRateAlt?.value ?? 0,
                overTimeRateAlt: margin?.overTimeRateAlt,
                shiftH: margin?.shiftH ?? 0,
                shiftM: margin?.shiftM,
                shiftAltH: margin?.shiftAltH ?? 0,
                shiftAltM: margin?.shiftAltM,
                shiftToH: margin?.shiftToH ?? 0,
                shiftToM: margin?.shiftToM,
                shiftAltToH: margin?.shiftAltToH ?? 0,
                shiftAltToM: margin?.shiftAltToM,
                isSubcontractor: margin?.isSubcontractor ?? false,
                reimOTSum: margin?.reimOTSum,
                reimMSum: margin?.reimMSum,
                professionId: margin?.clinician?.profession?.id ?? 0,
                otPremiumMargin: (margin?.premiumMargin ?? 0) * 100
            } as MarginCalculatorDetailModel,
            expenseAll: {
                subType: margin?.subType,
                pendingOfficialOffer: margin?.pendingOfficialOffer ? 1 : 0,
                certificationTextOther: margin?.reimbursements?.certificationTextOther,
                certificationValueOther: margin?.reimbursements?.educationReimbursements?.find(
                    (x) => x.id === CERT_OTHER_ID
                )
                    ? margin?.reimbursements?.certificationValueOther
                    : 0,
                onCallBillRate: margin?.onCallBillRate,
                onCallPayRate: margin?.onCallPayRate,
                chargeBillRate: margin?.chargeBillRate,
                chargePayRate: margin?.chargePayRate,
                callbackBillRate: margin?.callbackBillRate,
                callbackPayRate: margin?.callbackPayRate,
                callbackPayRateIncluded: margin.callbackPayRateIncluded,
                holidayBillRate: margin?.holidayBillRate,
                holidayPayRate: margin?.holidayPayRate,
                livingExpenses: margin?.livingExpenses,
                totalHours: margin?.totalHours,
                housingStipendPerShift: margin?.stipends?.housingStipendPerShift ?? 0,
                mealsPerShift: margin?.stipends?.mealStipendPerShift ?? 0,
                gasReimbursement: margin?.gasReimbursement,
                carShippingReimbursement: margin?.carShippingReimbursement,
                ayaBooster: margin?.ayaBooster,
                relocationType: margin?.relocationType,
                relocationTo: margin?.relocationTo,
                relocationFrom: margin?.relocationFrom,
                internalCost: margin?.internalCosts,
                internalCostNotes: margin?.internalCostNotes,
                moveInDate: margin?.housing?.moveInDate,
                moveOutDate: margin?.housing?.moveOutDate,
                isExtendedHousing: margin?.housing?.continuedHousing,
                taxableStipend: margin?.taxableStipend,
                nurseDeduction: margin?.nurseDeduction,
                nurseDeductionNotes: margin?.nurseDeductionNotes,
                isFamilyIncluded: margin?.housing?.isFamilyIncluded,
                familyRelationship: margin?.housing?.familyRelationship,
                familyName: margin?.housing?.familyName,
                familyAge: margin?.housing?.familyAge,
                familyNotes: margin?.housing?.familyNotes,
                isPetIncluded: margin?.housing?.isPetIncluded,
                petType: margin?.housing?.petType,
                petWeight: margin?.housing?.petWeight,
                petAge: margin?.housing?.petAge,
                petBreed: margin?.housing?.petBreed,
                housingType: margin?.housing?.housingType?.id,
                noOfBedrooms: margin?.housing?.bedrooms,
                isExtendedBenefits: margin?.isExtendedBenefits,
                benefitsProviderId: margin?.benefitsProviderId,
                isRapidResponseRates: margin?.isRapidResponseRates,
                isVacation: margin?.isVacation,
                isPendingFacilityApproval: margin?.isPendingFacilityApproval,
                vacationDates: margin?.vacationDates,
                estimatedWeeklyCheckPretax: margin?.estimatedWeeklyCheckPretax,
                reimbursementTotal: margin?.reimbursementTotal,
                orientationValue: margin?.orientationFirstHours ?? 0,
                orientationAmount: MarginCalculatorStateHelpers.convertOrientationAmountToModel(
                    margin?.orientationAmount ?? 0,
                    margin?.orientationType ?? ''
                ),
                orientationType: margin?.orientationType,
                orientationNextValue: margin?.orientationNextValue ?? 0,
                orientationNextAmount: MarginCalculatorStateHelpers.convertOrientationAmountToModel(
                    margin?.orientationNextAmount ?? 0,
                    margin?.orientationNextType ?? ''
                ),
                orientationNextType: margin?.orientationNextType,
                orientationAllHoursBillable: margin?.orientationAllHoursBillable,
                gsaMax: margin?.stipends?.weeklyStipends ?? 0,
                accountManager: margin?.accountManager,
                accountManagerId: margin?.accountManagerId,
                accountCoordinatorId: margin?.accountCoordinatorId,
                noOfMedicalDays: margin?.noOfMedicalDays,
                noOfDentalDays: margin?.noOfDentalDays,
                overtimeHours: margin?.overtimeHours,
                billDailyOtAfter: margin?.billDailyOtAfter,
                commisionRate: margin?.commisionRate,
                reimbursementOneTime: margin?.reimbursementOneTime,
                reimbursementMonthly: margin?.reimbursementMonthly,
                carRentalOneTime: margin?.reimbursements?.carRentalOneTime,
                fingerPrintingOneTime: margin?.reimbursements?.fingerPrintingOneTime,
                parkingOneTime: margin?.reimbursements?.parkingOneTime,
                parkingMonthly: margin?.reimbursements?.parkingMonthly,
                scrubsOneTime: margin?.reimbursements?.scrubsOneTime,
                taxableModulesOneTime: margin?.reimbursements?.taxableModulesOneTime,
                oneTimeCustomReimbursementAmount: margin?.reimbursements?.oneTimeCustomReimbursementAmount,
                oneTimeCustomReimbursementTitle: margin?.reimbursements?.oneTimeCustomReimbursementTitle,
                monthlyCustomReimbursementAmount: margin?.reimbursements?.monthlyCustomReimbursementAmount,
                monthlyCustomReimbursementTitle: margin?.reimbursements?.monthlyCustomReimbursementTitle,
                facilityPaidBonus: margin?.reimbursements?.facilityPaidBonus,
                discretionaryBonus: margin?.reimbursements?.discretionaryBonus,
                ayaPaidBonus: margin?.reimbursements?.ayaPaidBonus,
                referalBonus: margin?.reimbursements?.referralBonus,
                payToBonus: margin?.reimbursements?.payToBonus,
                forReferringBonus: margin?.reimbursements?.forReferringBonus,
                mealsPerWeek: margin?.stipends?.mealStipend ?? 0,
                housingStipendPerWeek: margin?.stipends?.housingStipend ?? 0,
                stateLicensures: margin?.reimbursements?.stateLicensures,
                educationReimbursements: margin?.reimbursements?.educationReimbursements?.filter(
                    (r) => r.id !== CERT_OTHER_ID
                ),
                housewares: margin?.housing?.housewares,
                selectedPickleAmount: margin?.reimbursements?.selectedPickleAmount,
                selectedPickleTitle: margin?.reimbursements?.selectedPickleTitle,
                pickledAmount: margin?.reimbursements?.pickledAmount,
                marginNotes: margin?.marginNotes,
                moveOutExpense: margin?.housing?.moveOutExpenses,
                marginType: margin?.marginType,
                jobId: margin?.jobId,
                agencyId: margin?.agencyId,
                agencyContactId: margin?.agencyContactId,
                agencyHolidayRate: margin?.agencyHolidayRate,
                facilityHolidayRate: margin?.facilityHolidayRate,
                agencyOvertimeRate: margin?.agencyOvertimeRate,
                facilityOvertimeRate: margin?.facilityOvertimeRate,
                agencyCallbackRate: margin?.agencyCallbackRate,
                facilityCallbackRate: margin?.facilityCallbackRate,
                agencyChargeRate: margin?.agencyChargeRate,
                facilityChargeRate: margin?.facilityChargeRate,
                agencyCallRate: margin?.agencyCallRate,
                facilityCallRate: margin?.facilityCallRate,
                recruiter: margin?.recruiter
            } as MarginCalculatorExpenseAllModel,
            insurance: margin?.isBenefit
                ? {
                      medicalEffectiveDate: margin?.medicalEffectiveDate
                          ? new Date(margin?.medicalEffectiveDate)
                          : null,
                      dentalEffectiveDate: margin?.dentalEffectiveDate ? new Date(margin?.dentalEffectiveDate) : null,
                      savedAfterMedicalDentalEffectiveInsurance: margin?.savedAfterMedicalDentalEffectiveInsurance
                  }
                : null
        } as MarginCalculatorModel;
    }

    static convertMarginToState(margin: MarginCalculatorModel): MarginState {
        return {
            id: margin?.id,
            fileName: margin?.fileName,
            marginPct: margin?.marginPct,
            marginDollars: margin?.marginDollars,
            baseBillingRate: { value: margin?.marginBillRate ?? 0, resetValidation: false } as BaseBillingRate,
            marginPayRate: margin?.marginPayRate,
            targetMargin: margin?.targetMargin,
            dateCreated: margin?.dateCreated?.toString(),
            createdBy: margin?.createdBy,
            dateModified: margin?.dateModified?.toString(),
            modifiedBy: margin?.modifiedBy,
            isDeleted: margin?.isDeleted,
            isPipelined: margin?.isPipelined,
            dateDeleted: margin?.dateDeleted?.toString(),
            deletedBy: margin?.deletedBy,
            version: margin?.version,
            contractId: margin?.contractId,
            contractStageCode: margin?.contractStageCode,
            contractStageDescription: margin?.contractStageDescription,
            datePipelined: margin?.datePipelined?.toString(),
            isLinkedContractRequestInDraftingStatus: margin?.isLinkedContractRequestInDraftingStatus,
            openContractRequestId: margin?.openContractRequestId,
            originalHospitalId: margin?.detail?.originalHospitalId,
            isOfferMargin: margin?.isOfferMargin,
            offerId: margin?.offerId,
            sampleMarginJobs: margin?.sampleMarginJobs,
            jobId: margin?.expenseAll?.jobId,
            duplicatedFromMarginId: margin?.duplicatedFromMarginId,
            importedFromMarginId: margin?.importedFromMarginId,
            // overview info
            clinician: {
                userId: margin?.userId
            } as Clinician,
            facility: {
                hospitalId: margin?.detail?.hospitalId,
                hospitalState: margin?.detail?.state
            } as Facility,
            specialty: {
                id: margin?.detail?.specialty
            } as ProfessionSpecialtyModel,
            pendingOfficialOffer: margin?.expenseAll?.pendingOfficialOffer === 1 ? true : false,

            // contract and shift info
            startDate: margin?.detail?.dateFrom?.toString(),
            endDate: margin?.detail?.dateTo?.toString(),
            weeks: margin?.detail?.weeks,
            lastWeeksLength: margin?.detail?.weeks,
            isVacation: margin?.expenseAll?.isVacation,

            // details
            isRate: margin?.detail?.isRate,
            isBillOverTime: margin?.detail?.isBillOverTime,
            billDorW: MarginCalculatorStateHelpers.convertBillingWeekOrDayRuleToState(margin?.detail?.billDorW),
            overTimeRate: margin?.detail?.overTimeRate,
            shifts: margin?.detail?.shifts,
            hours: margin?.detail?.hours,
            orientationHours: margin?.detail?.orientationHours,
            relocation: margin?.detail?.relocation,
            licenseReimbursement: margin?.detail?.licenseReimbursement,
            bonus: margin?.detail?.bonus,
            housingDeposit: margin?.detail?.housingDeposit,
            tittersPPD: margin?.detail?.tittersPPD,
            extendedStayHotel: margin?.detail?.extendedStayHotel,
            isCompBonus: margin?.detail?.isCompBonus,
            isExtension: margin?.detail?.isExtension,
            target: margin?.detail?.target,
            isBenefit: margin?.detail?.isBenefit,
            isMedical: margin?.detail?.isMedical,
            age: margin?.detail?.age,
            medicalCoverage: margin?.detail?.medicalCoverage,
            medicalMonth: margin?.detail?.medicalMonth,
            isDental: margin?.detail?.isDental,
            dentalCoverage: margin?.detail?.dentalCoverage,
            dentalMonth: margin?.detail?.dentalMonth,
            stipendB: margin?.detail?.stipendB,
            certReimb: margin?.detail?.certReimb,
            shipping: margin?.detail?.shipping,
            giftCard: margin?.detail?.giftCard,
            premiumRate: margin?.detail?.premiumRate,
            premiumBillRate: margin?.detail?.premiumBillRate,
            premiumMargin: (margin?.detail?.otPremiumMargin ?? 0) / 100,
            taxRate: margin?.detail?.estTaxRate,
            shiftTime: margin?.detail?.shiftTime,
            oTMult: margin?.detail?.oTMult,
            vmsFees: margin?.detail?.vmsFees,
            housing: {
                isAyaHousing: margin?.detail?.isAyaHousing,
                housingType: HousingStateHelpers.getHousingType(margin?.expenseAll?.housingType),
                continuedHousing: margin?.expenseAll?.isExtendedHousing,
                monthlyHousing: HousingStateHelpers.getMonthlyRent(margin),
                extendedStayHotel: HousingStateHelpers.getMonthlyRent(margin),
                moveInDate: margin?.expenseAll?.moveInDate,
                moveOutDate: margin?.expenseAll?.moveOutDate,
                bedrooms: margin?.expenseAll?.noOfBedrooms,
                moveOutExpenses: margin?.expenseAll?.moveOutExpense,
                furniture: margin?.detail?.furniture,
                furnitureDelivery: margin?.detail?.furnitureDelivery,
                utilities: margin?.detail?.utilities,
                isFamilyIncluded: margin?.expenseAll?.isFamilyIncluded,
                familyRelationship: margin?.expenseAll?.familyRelationship,
                familyName: margin?.expenseAll?.familyName,
                familyAge: margin?.expenseAll?.familyAge,
                familyNotes: margin?.expenseAll?.familyNotes,
                isPetIncluded: margin?.expenseAll?.isPetIncluded,
                petType: margin?.expenseAll?.petType,
                petWeight: margin?.expenseAll?.petWeight,
                petAge: margin?.expenseAll?.petAge,
                petBreed: margin?.expenseAll?.petBreed,
                housewares: margin?.expenseAll?.housewares
            },
            extDiscount: margin?.detail?.extDiscount,
            extDiscountType: margin?.detail?.extDiscountType,
            discountPercent: margin?.detail?.discountPercent,
            andOr: margin?.detail?.andOr,
            billDorWM: MarginCalculatorStateHelpers.convertBillingWeekOrDayRuleToState(margin?.detail?.billDorWM),
            nIH: margin?.detail?.nIH,
            finger: margin?.detail?.finger,
            hWares: margin?.detail?.hWares,
            altShift: margin?.detail?.altShift,
            shiftsAlt: margin?.detail?.shiftsAlt,
            hoursAlt: margin?.detail?.hoursAlt,
            shiftTimeAlt: margin?.detail?.shiftTimeAlt,
            baseBillingRateAlt: { value: margin?.detail?.billRateAlt ?? 0, resetValidation: false } as BaseBillingRate,
            overTimeRateAlt: margin?.detail?.overTimeRateAlt,
            shiftH: margin?.detail?.shiftH,
            shiftM: margin?.detail?.shiftM,
            shiftAltH: margin?.detail?.shiftAltH,
            shiftAltM: margin?.detail?.shiftAltM,
            shiftToH: margin?.detail?.shiftToH,
            shiftToM: margin?.detail?.shiftToM,
            shiftAltToH: margin?.detail?.shiftAltToH,
            shiftAltToM: margin?.detail?.shiftAltToM,
            isSubcontractor: margin?.detail?.isSubcontractor ?? false,
            reimOTSum: margin?.detail?.reimOTSum,
            reimMSum: margin?.detail?.reimMSum,

            // expenses
            subType: margin?.expenseAll?.subType,
            onCallBillRate: margin?.expenseAll?.onCallBillRate,
            onCallPayRate: margin?.expenseAll?.onCallPayRate,
            callbackPayRateIncluded: margin?.expenseAll?.callbackPayRateIncluded,
            chargeBillRate: margin?.expenseAll?.chargeBillRate,
            chargePayRate: margin?.expenseAll?.chargePayRate,
            callbackBillRate: margin?.expenseAll?.callbackBillRate,
            callbackPayRate: margin?.expenseAll?.callbackPayRate,
            holidayBillRate: margin?.expenseAll?.holidayBillRate,
            holidayPayRate: margin?.expenseAll?.holidayPayRate,
            livingExpenses: margin?.expenseAll?.livingExpenses,
            totalHours: margin?.expenseAll?.totalHours,
            gasReimbursement: margin?.expenseAll?.gasReimbursement,
            carShippingReimbursement: margin?.expenseAll?.carShippingReimbursement,
            ayaBooster: margin?.expenseAll?.ayaBooster,
            relocationType: margin?.expenseAll?.relocationType,
            relocationTo: margin?.expenseAll?.relocationTo,
            relocationFrom: margin?.expenseAll?.relocationFrom,
            internalCosts: margin?.expenseAll?.internalCost,
            internalCostNotes: margin?.expenseAll?.internalCostNotes,
            taxableStipend: margin?.expenseAll?.taxableStipend,
            nurseDeduction: margin?.expenseAll?.nurseDeduction,
            nurseDeductionNotes: margin?.expenseAll?.nurseDeductionNotes,
            isExtendedBenefits: margin?.expenseAll?.isExtendedBenefits,
            benefitsProviderId: margin?.expenseAll?.benefitsProviderId,
            isRapidResponseRates: margin?.expenseAll?.isRapidResponseRates,
            isPendingFacilityApproval: margin?.expenseAll?.isPendingFacilityApproval,
            vacationDates: margin?.expenseAll?.vacationDates,
            estimatedWeeklyCheckPretax: margin?.expenseAll?.estimatedWeeklyCheckPretax,
            reimbursementTotal: margin?.expenseAll?.reimbursementTotal ?? 0,
            orientationFirstHours: margin?.expenseAll?.orientationValue ?? 0,
            orientationAmount: +margin?.expenseAll?.orientationAmount.replace(/[^0-9.-]+/g, ''),
            orientationAmountIndicator: MarginCalculatorStateHelpers.convertOrientationAmountValueToModel(
                margin?.expenseAll?.orientationAmount
            ),
            orientationType: margin?.expenseAll?.orientationType,
            orientationNextValue: margin?.expenseAll?.orientationNextValue ?? 0,
            orientationNextAmount: +margin?.expenseAll?.orientationNextAmount.replace(/[^0-9.-]+/g, ''),
            orientationNextAmountIndicator: MarginCalculatorStateHelpers.convertOrientationAmountValueToModel(
                margin?.expenseAll?.orientationNextAmount
            ),
            orientationNextType: margin?.expenseAll?.orientationNextType,
            orientationAllHoursBillable: margin?.expenseAll?.orientationAllHoursBillable,
            accountManager: margin?.expenseAll?.accountManager,
            accountManagerId: margin?.expenseAll?.accountManagerId,
            accountCoordinatorId: margin?.expenseAll?.accountCoordinatorId,
            noOfMedicalDays: margin?.expenseAll?.noOfMedicalDays,
            noOfDentalDays: margin?.expenseAll?.noOfDentalDays,
            medicalEffectiveDate: margin?.insurance?.medicalEffectiveDate
                ? margin?.insurance?.medicalEffectiveDate?.toString()
                : null,
            dentalEffectiveDate: margin?.insurance?.dentalEffectiveDate
                ? margin?.insurance?.dentalEffectiveDate?.toString()
                : null,
            savedAfterMedicalDentalEffectiveInsurance: margin?.insurance?.savedAfterMedicalDentalEffectiveInsurance,
            overtimeHours: margin?.expenseAll?.overtimeHours,
            billDailyOtAfter: margin?.expenseAll?.billDailyOtAfter,
            commisionRate: margin?.expenseAll?.commisionRate,
            reimbursementOneTime: margin?.expenseAll?.reimbursementOneTime,
            reimbursementMonthly: margin?.expenseAll?.reimbursementMonthly,
            reimbursements: MarginCalculatorStateHelpers.convertToReimbursementState(margin),
            marginNotes: margin?.expenseAll?.marginNotes,
            marginType: margin?.expenseAll?.marginType,
            marginTypeId: margin?.marginTypeId,
            stipends: {
                mealStipend: margin?.expenseAll?.mealsPerWeek ?? 0,
                maxMeals: 0,
                housingStipend: margin?.expenseAll?.housingStipendPerWeek ?? 0,
                maxHousing: 0,
                stipendsPerContract: 0,
                weeklyStipends: margin?.expenseAll?.gsaMax ?? 0,
                maxStipends: 0,
                mealStipendPerShift: PayBillingStateHelpers.getStipendsPerShift(
                    margin?.expenseAll?.mealsPerWeek ?? 0,
                    margin?.detail?.shifts ?? 0
                ),
                housingStipendPerShift: PayBillingStateHelpers.getStipendsPerShift(
                    margin?.expenseAll?.housingStipendPerWeek ?? 0,
                    margin?.detail?.shifts ?? 0
                ),
                mealStipendPerDay:
                    margin?.detail?.meals ??
                    FormulaHelperService.roundNumber((margin?.expenseAll?.mealsPerWeek ?? 0) / 7),
                housingStipendPerDay: FormulaHelperService.roundNumber(
                    (margin?.expenseAll?.housingStipendPerWeek ?? 0) / 7
                )
            } as StipendState,

            //subcontractor expenses
            agencyId: margin?.expenseAll?.agencyId,
            agencyContactId: margin?.expenseAll?.agencyContactId,
            agencyHolidayRate: margin?.expenseAll?.agencyHolidayRate,
            facilityHolidayRate: margin?.expenseAll?.facilityHolidayRate,
            agencyOvertimeRate: margin?.expenseAll?.agencyOvertimeRate,
            facilityOvertimeRate: margin?.expenseAll?.facilityOvertimeRate,
            agencyCallbackRate: margin?.expenseAll?.agencyCallbackRate,
            facilityCallbackRate: margin?.expenseAll?.facilityCallbackRate,
            agencyChargeRate: margin?.expenseAll?.agencyChargeRate,
            facilityChargeRate: margin?.expenseAll?.facilityChargeRate,
            agencyCallRate: margin?.expenseAll?.agencyCallRate,
            facilityCallRate: margin?.expenseAll?.facilityCallRate,
            recruiter: margin?.expenseAll?.recruiter,
            overrideIneligibility: margin?.detail?.isBenefit,
            originalClinicianId: margin?.userId,
            originalProfessionId: margin?.detail?.professionId,
            originalSpecialtyId: margin?.detail?.specialty,
            previousProfessionId: margin?.detail?.professionId
        } as MarginState;
    }

    static convertToReimbursementState(margin: MarginCalculatorModel): ReimbursementsState {
        return {
            certificationTextOther: margin?.expenseAll?.certificationTextOther,
            certificationValueOther: margin?.expenseAll?.certificationValueOther,
            carRentalOneTime: margin?.expenseAll?.carRentalOneTime,
            fingerPrintingOneTime: margin?.expenseAll?.fingerPrintingOneTime,
            parkingOneTime: margin?.expenseAll?.parkingOneTime,
            parkingMonthly: margin?.expenseAll?.parkingMonthly,
            rentalCar: margin?.detail?.rentalCar,
            scrubsOneTime: margin?.expenseAll?.scrubsOneTime,
            taxableModulesOneTime: margin?.expenseAll?.taxableModulesOneTime,
            hasOneTimeCustomReimbursement: margin?.expenseAll?.oneTimeCustomReimbursementAmount > 0,
            oneTimeCustomReimbursementAmount: margin?.expenseAll?.oneTimeCustomReimbursementAmount,
            oneTimeCustomReimbursementTitle: margin?.expenseAll?.oneTimeCustomReimbursementTitle,
            hasMonthlyCustomReimbursement: margin?.expenseAll?.monthlyCustomReimbursementAmount > 0,
            monthlyCustomReimbursementAmount: margin?.expenseAll?.monthlyCustomReimbursementAmount,
            monthlyCustomReimbursementTitle: margin?.expenseAll?.monthlyCustomReimbursementTitle,
            facilityPaidBonus: margin?.expenseAll?.facilityPaidBonus,
            ayaPaidBonus: margin?.expenseAll?.ayaPaidBonus,
            discretionaryBonus: margin?.expenseAll?.discretionaryBonus,
            referralBonus: margin?.expenseAll?.referalBonus,
            payToBonus: margin?.expenseAll?.payToBonus,
            forReferringBonus: margin?.expenseAll?.forReferringBonus,
            stateLicensures: margin?.expenseAll?.stateLicensures,
            educationReimbursements: margin?.expenseAll?.certificationValueOther
                ? MarginCalculatorStateHelpers.addOtherReimbursement(
                      margin?.expenseAll?.educationReimbursements ?? [],
                      margin.expenseAll.certificationValueOther,
                      margin?.expenseAll?.certificationTextOther ?? CERT_OTHER_NAME
                  )
                : (margin?.expenseAll?.educationReimbursements ?? []),
            selectedPickleAmount: margin?.expenseAll?.selectedPickleAmount,
            selectedPickleTitle: margin?.expenseAll?.selectedPickleTitle,
            pickledAmount: margin?.expenseAll?.pickledAmount
        } as ReimbursementsState;
    }

    static convertJobFacilityToState(facility: Facility): Facility {
        return facility
            ? {
                  ...facility,
                  extDiscount: new Big(facility.extDiscount ?? 0).div(100).toNumber()
              }
            : null;
    }

    static convertJobToState(job: Job): MarginState {
        return {
            jobId: job?.jobId,
            facility: this.convertJobFacilityToState(job?.facility),
            startDate: new Date(job?.startDate).toLocaleDateString(),
            weeks: job?.duration ?? 13,
            altShift: (job?.isAlternateShift ?? false) ? 1 : 0,
            shifts: job?.shifts ?? 3,
            shiftsAlt: job?.alternateShifts ?? 3,
            hours: job?.hours ?? 12,
            hoursAlt: job?.alternateHours ?? 12,
            shiftTime: job?.shiftTime ?? 3,
            shiftTimeAlt: job?.shiftTimeAlt ?? 3,
            shiftH: job?.shiftH ?? 19,
            shiftM: job?.shiftM ?? 0,
            shiftToH: job?.shiftToH ?? 7,
            shiftToM: job?.shiftToM ?? 0,
            shiftAltH: job?.shiftAltH ?? 19,
            shiftAltM: job?.shiftAltM ?? 0,
            shiftAltToH: job?.shiftAltToH ?? 7,
            shiftAltToM: job?.shiftAltToM ?? 0,
            clinician: {
                profession: {
                    id: job?.professionId,
                    abbreviation: job.professionText
                }
            } as Clinician,
            reimbursements: {
                ayaPaidBonus: job?.ayaPaidBonus
            }
        } as MarginState;
    }

    static buildVacationCalendarCellCssClasses(
        vacationDates: Date[],
        contractFirstDate: Date,
        contractEndDate: Date,
        formDisabled: boolean
    ): (date: Date) => MatCalendarCellCssClasses {
        return (date: Date): MatCalendarCellCssClasses => {
            const isSameDate = (d1: Date, d2: Date): boolean => d1.getTime() === d2.getTime();
            const disabledCssClass = 'mat-calendar-body-disabled';
            const selectedCssClass = 'date-selected';
            const contractStartEndCssClass = 'date-contract-start-or-end';

            // Handle null or undefined vacationDates
            if (!vacationDates || vacationDates.length === 0) {
                return formDisabled ? disabledCssClass : '';
            }

            const [vacationFirstDate, vacationLastDate] = [vacationDates[0], vacationDates[vacationDates.length - 1]];

            const foundDate = vacationDates.find((vacationDate) => isSameDate(vacationDate, date));
            const isVacationFirstDate = isSameDate(vacationFirstDate, date);
            const isVacationLastDate = isSameDate(vacationLastDate, date);
            const isContractStartDate = isSameDate(contractFirstDate, date);
            const isContractEndDate = isSameDate(contractEndDate, date);

            // Handle vacation dates
            if (foundDate) {
                const isStartOrEndMatch =
                    (isContractStartDate && isVacationFirstDate) || (isContractEndDate && isVacationLastDate);

                if (isStartOrEndMatch) {
                    return contractStartEndCssClass;
                }

                return formDisabled ? [selectedCssClass, disabledCssClass] : selectedCssClass;
            }

            // Handle non-vacation dates
            return formDisabled ? disabledCssClass : '';
        };
    }

    static buildDateRangeDisplay(vacationDates: Date[]): string {
        if (vacationDates.length === 0) {
            return '';
        }

        if (vacationDates.length === 1) {
            return dayjs(vacationDates[0]).format('M/D/YY');
        }

        const { dateRangeSeq, nonDateRangeSeq } = this.buildMultipleDateRangeDisplay(vacationDates);

        if (nonDateRangeSeq !== '' && dateRangeSeq === '') {
            return nonDateRangeSeq;
        }

        if (dateRangeSeq !== '' && nonDateRangeSeq === '') {
            return dateRangeSeq;
        }

        return `${nonDateRangeSeq}, ${dateRangeSeq}`;
    }

    static combinedDateRangeDisplay(vacationDates: Date[]): string {
        if (vacationDates && vacationDates.length > 0) {
            // Sort dates chronologically
            const sortedDates = [...vacationDates].sort((a, b) => a.getTime() - b.getTime());

            // Group consecutive dates
            const ranges: Date[][] = [];
            let currentRange: Date[] = [];

            for (const date of sortedDates) {
                if (currentRange.length === 0) {
                    currentRange.push(date);
                } else {
                    const prevDate = currentRange[currentRange.length - 1];
                    if (dayjs(date).diff(prevDate, 'day') === 1) {
                        currentRange.push(date);
                    } else {
                        ranges.push(currentRange);
                        currentRange = [date];
                    }
                }
            }
            if (currentRange.length > 0) {
                ranges.push(currentRange);
            }

            // Format ranges as strings
            const result = ranges
                .map((range) => {
                    if (range.length === 1) {
                        return dayjs(range[0]).format(dateFormat);
                    } else {
                        return `${dayjs(range[0]).format(dateFormat)}-${dayjs(range[range.length - 1]).format(dateFormat)}`;
                    }
                })
                .join(', ');

            return result;
        }

        return '';
    }

    static convertAndOrRuleToModel(andOrRule: string): string {
        return andOrRule === '-' ? '' : andOrRule;
    }

    static findLongestConsecutiveDates(dates: Date[]): number {
        if (dates.length === 0) {
            return 0;
        }

        dates.sort((a, b) => a.getTime() - b.getTime());

        let longestStreak = 0;
        let currentStreak = 1;

        for (let i = 1; i < dates.length; i++) {
            const currentDate = dates[i];
            const previousDate = dates[i - 1];

            if (dayjs(currentDate).diff(previousDate, 'day') === 1) {
                currentStreak++;
            } else {
                longestStreak = Math.max(longestStreak, currentStreak);
                currentStreak = 1;
            }
        }

        longestStreak = Math.max(longestStreak, currentStreak);

        return longestStreak;
    }

    private static buildMultipleDateRangeDisplay(vacationDates: Date[]): {
        dateRangeSeq: string;
        nonDateRangeSeq: string;
    } {
        const dateRangeSet = new Set<Date[]>();
        const nonDateRangeSet = new Set<Date[]>();
        let currentConsecutive: Date[] = [];

        vacationDates.forEach((value) => {
            if (currentConsecutive.length === 0) {
                currentConsecutive.push(value);
                nonDateRangeSet.add(currentConsecutive);
                return;
            }

            const lastConsecutivePair = currentConsecutive[currentConsecutive.length - 1];

            if (dayjs(value).diff(lastConsecutivePair, 'day') === 1) {
                currentConsecutive.push(value);
            } else {
                currentConsecutive = [value];
            }

            // We have a two or more day pair so add it
            // to the dateRanges array and filter out
            // duplicates from nonDateRanges to keep single dates available
            if (currentConsecutive.length > 1) {
                dateRangeSet.add(currentConsecutive);

                for (const element of nonDateRangeSet) {
                    // Filter out dates from the non consecutive date range
                    if (dateRangeSet.has(element)) {
                        nonDateRangeSet.delete(element);
                    }
                }
            } else {
                nonDateRangeSet.add(currentConsecutive);
            }
        });

        const dateRangeSeq = this.dateRangeSequence(dateRangeSet, false);
        const nonDateRangeSeq = this.dateRangeSequence(nonDateRangeSet, true);
        return { dateRangeSeq, nonDateRangeSeq };
    }

    private static dateRangeSequence(values: Set<Date[]>, nonConsecutive: boolean): string {
        let count = 0;
        let result = '';

        for (const element of values) {
            const array = nonConsecutive ? [] : Array.of(element)[0].map((date) => dayjs(date).format(dateFormat));

            result += nonConsecutive ? dayjs(element[0]).format(dateFormat) : `${array[0]}-${array[array.length - 1]}`;

            if (count !== values.size - 1) {
                result += ', ';
            }

            count++;
        }

        return result;
    }

    private static convertBillingWeekOrDayRuleToState(rule: string): string {
        switch (rule) {
            case 'D':
                return 'day';
            case 'W':
                return 'week';
            case '2':
                return '2weeks';
            default:
                return '-';
        }
    }

    private static convertBillingWeekOrDayRuleFromState(rule: string): string {
        switch (rule) {
            case 'day':
                return 'D';
            case 'week':
                return 'W';
            case '2weeks':
                return '2';
            default:
                return '';
        }
    }

    private static addOtherReimbursement(
        educationReimbursements: EducationReimbursement[],
        otherValue: number,
        otherText: string
    ): EducationReimbursement[] {
        return [...educationReimbursements, { id: CERT_OTHER_ID, name: otherText, amount: otherValue }];
    }

    static correctMinutes(value: number): string {
        return value !== null && value !== undefined && Number(value) === 0 ? '00' : value?.toString();
    }

    static convertOrientationAmountToModel(amount: number, type: string): string {
        if (type.includes('$')) {
            return NumberHelperService.formatCurrency(amount);
        } else if (type.includes('%')) {
            return NumberHelperService.formatPercent(new Big(amount).div(100).toNumber());
        }
        return NumberHelperService.formatNumberToPrecision(amount, 2);
    }

    static convertOrientationTypeFromModel(amount: string): string {
        return amount.includes('$') ? '$' : '%';
    }

    static getBenefitCoverage(isBenefit: boolean): string {
        return isBenefit ? '1' : '';
    }

    static getTotalMonths(weeks: number): number {
        return new Big(weeks).div(4).round(0, Big.roundDown).toNumber();
    }

    static convertOrientationAmountValueToModel(value: string): string {
        if (value?.includes('$')) {
            return '$';
        } else if (value?.includes('%')) {
            return '%';
        }
        return '';
    }

    static isSameDate(d1: Date | null, d2: Date | null): boolean {
        return (
            !!d1 &&
            !!d2 &&
            d1.getFullYear() === d2.getFullYear() &&
            d1.getMonth() === d2.getMonth() &&
            d1.getDate() === d2.getDate()
        );
    }

    static isCaViAk(state: string): boolean {
        const caStates = ['CA', 'AK', 'VI'];
        return state ? caStates.includes(state) : false;
    }

    static isBeforeDate(d1: Date | null, d2: Date | null): boolean {
        return !!d1 && !!d2 && dayjs(d1).isBefore(d2);
    }
}
